import { GemsChip } from "@/components/WeeklyRewards/GemsChip";
import type { UserActivity } from "@/types/user-activities";
import { formatDate } from "@/utils/date";
import { Diamond } from "@mui/icons-material";
import { ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import type React from "react";
import { useMemo } from "react";

interface ActivityListItemProps {
  activity: UserActivity;
}

const getDisplayName = (
  questName?: string,
  sourceEarningType?: string,
): string | undefined => {
  if (questName?.toLowerCase()?.includes("daily-feed")) {
    return "Daily Feed";
  }

  if (questName?.toLowerCase()?.includes("discover-feed")) {
    return "Discover Feed";
  }

  if (sourceEarningType === "sweepstake_ticket") {
    return `${questName} Rewards`;
  }
  if (sourceEarningType === "chest") {
    return `${questName} Opened Chest`;
  }

  return questName;
};

export const ActivityListItem: React.FC<ActivityListItemProps> = ({
  activity,
}) => {
  const { reward, game_name, occurred_at, quest_name } = activity;

  const timeAgo = useMemo(
    () => formatDistanceToNow(new Date(occurred_at), { addSuffix: true }),
    [occurred_at],
  );

  const activityTitle = useMemo(
    () =>
      getDisplayName(quest_name ?? undefined, reward.source_earning_type) ?? "",
    [quest_name, reward.source_earning_type],
  );

  const secondaryText = useMemo(() => {
    // Don't show game name if it's "Immutable" or null/empty
    if (!game_name || game_name === "Immutable") {
      return timeAgo;
    }
    return `${game_name} • ${timeAgo}`;
  }, [game_name, timeAgo]);

  return (
    <ListItem>
      <ListItemIcon
        sx={{
          minWidth: "48px",
        }}
      >
        <Diamond />
      </ListItemIcon>

      <ListItemText
        primary={activityTitle}
        secondary={secondaryText}
        slotProps={{
          primary: {
            sx: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          },
          secondary: {
            title: formatDate(new Date(occurred_at), true),
          },
        }}
      />

      <GemsChip
        amount={reward.amount}
        completed={reward.amount > 0}
        sx={{ ml: 2 }}
      />
    </ListItem>
  );
};
